import React, { useEffect, useRef, useState } from 'react';
import { Answer, QuestionGroup, Questions } from '../../Models';
import { ChevronDownIcon, ChevronUpIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/20/solid';
import ConfirmModal from '../Shared/ConfirmModal';
import { useNotification } from '../../context/NotificationContext';
import parse from 'html-react-parser'
import Loading from '../Shared/Loading';
import QuestionAnalysis from './QuestionAnalysis';
import { useNavigate } from 'react-router-dom';
import { deleteQuestions, getQuestionById } from '../../api/assessmentquestion.api';
import useApiToken from '../../hooks/useApiToken';

export interface QuestionDetails {
  answeredCorrectly: number,
  answeredIncorrectly: number,
  unanswered: number,
  questionGroup: QuestionGroup | null,
  answers: Answer[] | null;
  accuracy: number;
}

const QuestionCard = ({ question, selected,
  handleSelect, getSelectedEditQuestion, onQuestionExpand, questionExpandId, index }: {
    question: Questions;
    selected: boolean;
    handleSelect: () => void;
    getSelectedEditQuestion: Function,
    onQuestionExpand: Function,
    questionExpandId: string,
    index: number,
  }) => {
  const token = useApiToken();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const { success, error: notifyError } = useNotification();
  const { id, text, type, tags, time, accuracy, imageUrl } = question;
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  // Debug log to check if imageUrl exists
  useEffect(() => {
    if (type.toUpperCase() === 'IMAGE') {
      console.log('IMAGE Question:', { id, type, imageUrl, hasImageUrl: !!imageUrl });
    }
  }, [type, imageUrl, id]);

  // Hover delay timers
  const hoverEnterTimer = useRef<NodeJS.Timeout | null>(null);
  const hoverLeaveTimer = useRef<NodeJS.Timeout | null>(null);

  const handleEdit = () => {
    getSelectedEditQuestion(id);
  }

  const handleQuestionClick = () => {
    handleEdit()
    // navigate(`/questions/edit/${id}`);
  }

  const [questionDetails, setQuestionDetails] = useState<QuestionDetails>({
    answeredCorrectly: 0,
    answeredIncorrectly: 0,
    unanswered: 0,
    questionGroup: null,
    answers: null,
    accuracy: 0
  })

  const getQuestionDetails = async () => {
    if (!token) return;
    setLoading(true);
    await getQuestionById(id, token).then((res) => {
      setQuestionDetails({
        answeredCorrectly: res.answeredCorrectly,
        answeredIncorrectly: res.answeredIncorrectly,
        unanswered: res.unanswered,
        questionGroup: res.questionGroup,
        answers: res.answers,
        accuracy: res.accuracy
      })
      setLoading(false);
    })
  }

  const handleQuestionHoverEnter = () => {
    if (hoverLeaveTimer.current) {
      clearTimeout(hoverLeaveTimer.current);
      hoverLeaveTimer.current = null;
    }

    hoverEnterTimer.current = setTimeout(() => {
      if (questionExpandId !== question.id) {
        onQuestionExpand(question.id);
        getQuestionDetails();
      }
    }, 300);
  }

  const handleQuestionHoverLeave = (e: React.MouseEvent) => {
    if (hoverEnterTimer.current) {
      clearTimeout(hoverEnterTimer.current);
      hoverEnterTimer.current = null;
    }

    const relatedTarget = e.relatedTarget;
    if (relatedTarget &&
      relatedTarget instanceof Node &&
      modalRef.current &&
      modalRef.current.contains(relatedTarget)) {
      return;
    }

    hoverLeaveTimer.current = setTimeout(() => {
      onQuestionExpand('');
    }, 300);
  }

  const handleModalHoverEnter = () => {
    if (hoverLeaveTimer.current) {
      clearTimeout(hoverLeaveTimer.current);
      hoverLeaveTimer.current = null;
    }
  }

  const handleModalHoverLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget;
    const questionTextContainer = modalRef.current?.parentElement;

    if (relatedTarget &&
      relatedTarget instanceof Node &&
      questionTextContainer &&
      questionTextContainer.contains(relatedTarget)) {
      hoverLeaveTimer.current = setTimeout(() => {
        onQuestionExpand('');
      }, 100);
    } else {
      onQuestionExpand('');
    }
  }

  const handleDelete = () => {
    setIsModalOpen(true);
  }

  const confirmDelete = async () => {
    setIsModalOpen(false);
    if (!token) return;
    try {
      await deleteQuestions([id], token);
      success("Success", "The question was successfully deleted");
      setTimeout(() => {
        window.location.reload();
      }, 2000)
    } catch (err: any) {
      notifyError("Error", err?.message || "Unable to delete question");
    }
  };

  const [modalTop, setModalTop] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (modalRef.current) {
      const viewportHeight = window.innerHeight;
      const modalBottomPosition =
        modalRef.current.getBoundingClientRect().bottom + 220;
      if (modalBottomPosition > viewportHeight) {
        setModalTop(true);
      } else {
        setModalTop(false);
      }
    }
  }, [questionExpandId]);

  useEffect(() => {
    return () => {
      if (hoverEnterTimer.current) {
        clearTimeout(hoverEnterTimer.current);
      }
      if (hoverLeaveTimer.current) {
        clearTimeout(hoverLeaveTimer.current);
      }
    };
  }, []);

  return (
    <>
      <tr className="cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1c2229] border-t dark:border-gray-500 rounded-sm">
        <td className="px-4 py-4">
          <input
            type="checkbox"
            checked={selected}
            onChange={handleSelect}
            className="form-checkbox z-[11] bg-transparent left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 "
          />
        </td>
        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white relative">
          <div
            className="text-left dark:text-gray-300 relative"
            ref={modalRef}
            onMouseEnter={handleQuestionHoverEnter}
            onMouseLeave={handleQuestionHoverLeave}
          >
            {(() => {
              const isImageType = type.toUpperCase() === 'IMAGE';
              const hasImageUrl = !!imageUrl;
              console.log('Render check:', { id, type, isImageType, imageUrl, hasImageUrl });

              if (isImageType && hasImageUrl) {
                return (
                  <div
                    onClick={handleQuestionClick}
                    className="cursor-pointer hover:opacity-80"
                  >
                    <img
                      src={imageUrl}
                      alt="Question Image"
                      className="max-w-[200px] max-h-[150px] rounded border border-gray-300 dark:border-gray-600 object-contain"
                      onError={(e) => console.error('Image load error:', imageUrl)}
                    />
                  </div>
                );
              }

              if (text.length > 200) {
                if (expanded) {
                  return (
                    <div className="flex flex-col items-center">
                      <div
                        onClick={handleQuestionClick}
                        className="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                      >
                        {parse(text.replace(/\s*style="[^"]*"/g, ''))}
                      </div>
                      <ChevronUpIcon
                        height={17}
                        color="gray"
                        onClick={() => setExpanded(false)}
                        className="cursor-pointer mt-2 dark:hover:text-white"
                      />
                    </div>
                  );
                } else {
                  return (
                    <div className="flex flex-col items-center">
                      <div
                        onClick={handleQuestionClick}
                        className="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                      >
                        {`${text.replace(/\s*style="[^"]*"/g, '').substring(0, 200)}...`}
                      </div>
                      <ChevronDownIcon
                        height={17}
                        color="gray"
                        onClick={() => setExpanded(true)}
                        className="cursor-pointer mt-2 dark:hover:text-white"
                      />
                    </div>
                  );
                }
              }

              return (
                <div
                  onClick={handleQuestionClick}
                  className="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                >
                  {parse(text.replace(/\s*style="[^"]*"/g, ''))}
                </div>
              );
            })()}
            {questionExpandId === question.id && (
              <div
                className={`absolute 
                 left-6
                  ${modalTop ? "bottom-4" : "top-4"}  mt-2 dark:bg-darkbglight dark:text-white bg-white text-black text-xs p-3 rounded-md shadow-lg z-30 absolute min-w-[300px]`}
                onMouseEnter={handleModalHoverEnter}
                onMouseLeave={handleModalHoverLeave}
                onClick={(e) => e.stopPropagation()}
              >
                {!loading ?
                  <QuestionAnalysis
                    Question={question}
                    QuestionDetails={questionDetails}
                  /> : <Loading />}
              </div>
            )}
          </div>
        </td>
        <td className="px-6 py-4 hidden sm:table-cell">
          <div
            className="flex flex-row flex-wrap overflow-x-auto p-2 gap-1 justify-center"
            style={{ maxHeight: '100px' }}
          >
            {question.skills?.map((s, index) => (
              <div key={index} className="mr-2">
                <span
                  className={`dark:bg-gray-600 bg-gray-200 px-2 py-[2px] rounded-md text-xs truncate max-w-40 dark:text-white`}
                >
                  {s.name}
                </span>
              </div>
            ))}
          </div>
        </td>
        <td className="px-6 py-4 hidden sm:table-cell text-center">
          {(type === 'audio' ||
            type === 'text' ||
            type === 'video' ||
            (accuracy !== 0 && !accuracy) || accuracy === -1) ? (
            <small> N/A </small>
          ) : (
            accuracy.toFixed(2) + '%'
          )}
        </td>
        <td className="px-6 py-4 text-center">
          <div className="flex justify-center items-center">
            <PencilSquareIcon
              className="font-medium text-gray-400 cursor-pointer dark:text-blue-500 hover:underline"
              onClick={handleEdit}
              height={20}
            />
            <span className="mx-2">|</span>
            <TrashIcon
              className="font-medium text-red-600 cursor-pointer dark:text-red-500 hover:underline"
              onClick={handleDelete}
              height={20}
            />
          </div>
        </td>
      </tr>

      <ConfirmModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        onConfirm={confirmDelete}
        type="danger"
        title="Delete Question"
        description="Are you sure you want to delete this question? This action cannot be undone."
      />
    </>
  );
};

export default QuestionCard;